import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Lock,
  Unlock,
  Eye,
  KeyRound,
  ChevronRight,
  CreditCard,
  Settings,
  Gauge,
  Plus,
  Check,
  X,
  Wallet,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";
import NavBar from "../components/NavBar";
import { TrustBadge, SecurityNote, ConfirmationNote } from "../components/EmptyState";

const VirtualCard = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [revealedCard, setRevealedCard] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // "reveal" or "pin"
  const [showTopUp, setShowTopUp] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showVerifyCard, setShowVerifyCard] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null);
  const [verificationAmount, setVerificationAmount] = useState("");
  const [topUpAmount, setTopUpAmount] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [cardForm, setCardForm] = useState({
    card_number: "",
    expiry: "",
    cvv: "",
    cardholder_name: ""
  });

  // Fetch virtual card
  const { data: card, isLoading } = useQuery({
    queryKey: ["virtualCard"],
    queryFn: async () => {
      const response = await axios.get(`${API}/card`);
      return response.data;
    }
  });

  // Fetch health score for Order Physical Card eligibility
  const { data: healthScore } = useQuery({
    queryKey: ["healthScore"],
    queryFn: async () => {
      const response = await axios.get(`${API}/ai/health-score`);
      return response.data;
    }
  });

  // Fetch payment methods
  const { data: paymentMethods } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: async () => {
      const response = await axios.get(`${API}/topup/payment-methods`);
      return response.data;
    }
  });

  // Fetch pending verification methods
  const { data: pendingMethods, refetch: refetchPending } = useQuery({
    queryKey: ["pendingPaymentMethods"],
    queryFn: async () => {
      const response = await axios.get(`${API}/topup/payment-methods/pending`);
      return response.data;
    }
  });

  // Fetch wallets
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Reveal card mutation
  const revealMutation = useMutation({
    mutationFn: async (pin) => {
      const response = await axios.post(`${API}/card/reveal?pin=${pin}`);
      return response.data;
    },
    onSuccess: (data) => {
      setRevealedCard(data);
      setShowCardDetails(true);
      setShowPinModal(false);
      setPinInput("");
      toast.success("Card details revealed");
      // Auto-hide after 30 seconds
      setTimeout(() => {
        setShowCardDetails(false);
        setRevealedCard(null);
      }, 30000);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Invalid PIN");
    }
  });

  // Freeze/unfreeze mutations
  const freezeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API}/card/freeze`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["virtualCard"]);
      toast.success("Card frozen");
    }
  });

  const unfreezeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API}/card/unfreeze`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["virtualCard"]);
      toast.success("Card unfrozen");
    }
  });

  // Initiate payment method (with verification)
  const addMethodMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup/payment-method/initiate`, cardForm);
      return response.data;
    },
    onSuccess: (data) => {
      setShowAddCard(false);
      setCardForm({ card_number: "", expiry: "", cvv: "", cardholder_name: "" });
      setPendingVerification(data);
      setShowVerifyCard(true);
      refetchPending();
      toast.success("Card added! Please verify the charge amount.");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add card");
    }
  });

  // Verify payment method
  const verifyMethodMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup/payment-method/verify`, {
        method_id: pendingVerification?.method_id,
        verification_amount: parseFloat(verificationAmount)
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentMethods"]);
      refetchPending();
      setShowVerifyCard(false);
      setPendingVerification(null);
      setVerificationAmount("");
      toast.success("Card verified successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  });

  // Delete payment method
  const deleteMethodMutation = useMutation({
    mutationFn: async (methodId) => {
      await axios.delete(`${API}/topup/payment-method/${methodId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentMethods"]);
      refetchPending();
      toast.success("Card removed");
    }
  });

  // Top-up mutation
  const topUpMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/topup`, {
        amount: parseFloat(topUpAmount),
        currency: "JMD",
        payment_method_id: selectedMethod,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      setShowTopUp(false);
      setTopUpAmount("");
      toast.success(`J$${data.net_amount.toLocaleString()} added to wallet!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Top-up failed");
    }
  });

  const handlePinSubmit = () => {
    if (pinInput.length !== 4) {
      toast.error("Enter your 4-digit PIN");
      return;
    }
    if (pendingAction === "reveal") {
      revealMutation.mutate(pinInput);
    } else if (pendingAction === "pin") {
      // Show PIN (mocked)
      toast.success("Your PIN is: 1234");
      setShowPinModal(false);
      setPinInput("");
    }
  };

  const openPinModal = (action) => {
    setPendingAction(action);
    setShowPinModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ""));
    toast.success("Copied to clipboard!");
  };

  const formatCurrency = (amount) => {
    return `J$${parseFloat(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const jmdWallet = wallets?.find(w => w.currency === "JMD");
  const canOrderPhysicalCard = (healthScore?.score || 0) >= 75;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="virtual-card-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Stelx Card</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Virtual Card Display - Premium Dark Gradient */}
        <div 
          className={`relative rounded-2xl p-6 overflow-hidden transition-all duration-300 ${
            card?.status === "frozen" 
              ? "bg-gradient-to-br from-slate-500 via-slate-600 to-slate-700" 
              : "bg-gradient-to-br from-slate-800 via-slate-900 to-black"
          }`}
          style={{
            boxShadow: card?.status === "frozen" 
              ? "0 8px 32px rgba(100, 116, 139, 0.3)" 
              : "0 8px 32px rgba(0, 0, 0, 0.5)"
          }}
          data-testid="virtual-card-display"
        >
          {/* Stelx branding & VIRTUAL badge */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <span className="text-white font-heading font-bold text-lg tracking-wide">Stelx</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <span className="text-[10px] uppercase tracking-widest text-white/80 font-medium">Virtual</span>
              </div>
            </div>
          </div>

          {/* EMV Chip */}
          <div className="mb-6">
            <div className="w-12 h-9 rounded-md bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-600 shadow-lg">
              <div className="w-full h-full rounded-md bg-gradient-to-br from-yellow-200/20 to-transparent" />
            </div>
          </div>

          {/* Card Number */}
          <div className="mb-6">
            <p className="font-mono text-xl tracking-[0.2em] text-white">
              {showCardDetails && revealedCard 
                ? revealedCard.card_number 
                : card?.card_number_masked || "•••• •••• •••• ••••"}
            </p>
          </div>

          {/* Expiry, CVV and VISA */}
          <div className="flex justify-between items-end">
            <div className="flex gap-8">
              <div>
                <p className="text-[10px] text-white/50 uppercase tracking-widest mb-1">Valid Thru</p>
                <p className="font-mono text-white text-sm">
                  {showCardDetails && revealedCard ? revealedCard.expiry : card?.expiry_masked || "••/••"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-white/50 uppercase tracking-widest mb-1">CVV</p>
                <p className="font-mono text-white text-sm">
                  {showCardDetails && revealedCard ? revealedCard.cvv : "•••"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-white tracking-wider italic">VISA</span>
              <p className="text-[10px] text-white/60 uppercase mt-0.5">Debit</p>
            </div>
          </div>

          {/* Frozen overlay */}
          {card?.status === "frozen" && (
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center">
              <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                <span className="text-white text-sm font-medium">Card Frozen</span>
              </div>
            </div>
          )}
        </div>

        {/* Card Status */}
        <div className="text-center">
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
            card?.status === "frozen" 
              ? "bg-slate-500/20 text-slate-400" 
              : "bg-emerald-500/20 text-emerald-400"
          }`}>
            {card?.status === "frozen" ? "Card Frozen" : "Ready to use"}
          </span>
        </div>

        {/* Primary Actions - Icon Buttons */}
        <div className="flex justify-center gap-6">
          {/* Lock/Unlock */}
          <button
            onClick={() => card?.status === "frozen" ? unfreezeMutation.mutate() : freezeMutation.mutate()}
            disabled={freezeMutation.isPending || unfreezeMutation.isPending}
            className="flex flex-col items-center gap-2 group"
            data-testid="freeze-toggle-btn"
          >
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all ${
              card?.status === "frozen" 
                ? "bg-emerald-500/10 border border-emerald-500/30 group-hover:bg-emerald-500/20" 
                : "bg-primary/10 border border-primary/30 group-hover:bg-primary/20"
            }`}>
              {card?.status === "frozen" ? (
                <Unlock className="w-6 h-6 text-emerald-400" />
              ) : (
                <Lock className="w-6 h-6 text-primary" />
              )}
            </div>
            <span className={`text-xs font-medium ${
              card?.status === "frozen" ? "text-emerald-400" : "text-primary"
            }`}>
              {card?.status === "frozen" ? "Unlock" : "Lock card"}
            </span>
          </button>

          {/* Show Details */}
          <button
            onClick={() => openPinModal("reveal")}
            disabled={revealMutation.isPending}
            className="flex flex-col items-center gap-2 group"
            data-testid="reveal-details-btn"
          >
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center transition-all group-hover:bg-primary/20">
              <Eye className="w-6 h-6 text-primary" />
            </div>
            <span className="text-xs font-medium text-primary">
              {showCardDetails ? "Hide details" : "Show details"}
            </span>
          </button>

          {/* Show PIN */}
          <button
            onClick={() => openPinModal("pin")}
            className="flex flex-col items-center gap-2 group"
            data-testid="show-pin-btn"
          >
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center transition-all group-hover:bg-primary/20">
              <KeyRound className="w-6 h-6 text-primary" />
            </div>
            <span className="text-xs font-medium text-primary">Show PIN</span>
          </button>
        </div>

        {/* Trust microcopy for card actions */}
        <TrustBadge
          icon={Shield}
          text="Card actions take effect instantly. You're always in control."
          variant="security"
        />

        {/* Card Controls Section */}
        <div className="glass rounded-2xl overflow-hidden">
          {/* Spending Limits */}
          <button 
            onClick={() => navigate("/card/spending-limits")}
            className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors border-b border-white/5"
            data-testid="spending-limits-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Spending limits</p>
                <p className="text-xs text-muted-foreground">Daily purchases and withdrawal limits</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* Card Settings */}
          <button 
            onClick={() => navigate("/card/settings")}
            className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            data-testid="card-settings-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Settings</p>
                <p className="text-xs text-muted-foreground">Card usage, preferences</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Order Physical Card - Conditional */}
        <div className="glass rounded-2xl overflow-hidden">
          <button 
            className={`w-full flex items-center justify-between p-4 ${
              canOrderPhysicalCard ? "hover:bg-white/5" : "opacity-60 cursor-not-allowed"
            } transition-colors`}
            disabled={!canOrderPhysicalCard}
            data-testid="order-physical-card-btn"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                canOrderPhysicalCard ? "bg-emerald-500/10" : "bg-muted/10"
              }`}>
                <CreditCard className={`w-5 h-5 ${canOrderPhysicalCard ? "text-emerald-400" : "text-muted-foreground"}`} />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Order Physical Card</p>
                {canOrderPhysicalCard ? (
                  <p className="text-xs text-emerald-400">You&apos;re eligible! Order now</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Available when Health Score reaches 75+
                  </p>
                )}
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 ${canOrderPhysicalCard ? "text-muted-foreground" : "text-muted-foreground/50"}`} />
          </button>
        </div>
      </main>

      {/* PIN Entry Modal */}
      {showPinModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowPinModal(false);
            setPinInput("");
          }}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-center mb-2">
              Enter PIN
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              {pendingAction === "reveal" 
                ? "Enter your 4-digit PIN to reveal card details" 
                : "Enter your 4-digit PIN to view card PIN"}
            </p>

            <Input
              type="password"
              maxLength={4}
              inputMode="numeric"
              pattern="[0-9]*"
              className="input-glass text-center text-2xl tracking-[0.5em] font-mono h-14 mb-6"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••"
              autoFocus
              data-testid="pin-input"
            />

            <p className="text-xs text-muted-foreground text-center mb-4">
              Demo PIN: 1234
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary"
                onClick={() => {
                  setShowPinModal(false);
                  setPinInput("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={handlePinSubmit}
                disabled={revealMutation.isPending}
                data-testid="confirm-pin-btn"
              >
                {revealMutation.isPending ? "Verifying..." : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top-Up Modal */}
      {showTopUp && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowTopUp(false)}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg mb-4">Top Up Wallet</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Amount</Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
                  <Input
                    type="number"
                    placeholder="0.00"
                    className="input-glass pl-10 font-mono"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    data-testid="topup-amount-input"
                  />
                </div>
              </div>

              {paymentMethods && paymentMethods.length > 0 && (
                <div>
                  <Label>Select Card</Label>
                  <div className="space-y-2 mt-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.method_id}
                        onClick={() => setSelectedMethod(method.method_id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                          selectedMethod === method.method_id
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-white/5"
                        }`}
                      >
                        <CreditCard className="w-5 h-5" />
                        <span className="font-mono text-sm">{method.brand} •••• {method.last4}</span>
                        {selectedMethod === method.method_id && (
                          <Check className="w-5 h-5 text-primary ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(!paymentMethods || paymentMethods.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Add a card first to top up
                </p>
              )}

              <Button
                className="w-full btn-primary h-12"
                onClick={() => topUpMutation.mutate()}
                disabled={!topUpAmount || !selectedMethod || topUpMutation.isPending}
                data-testid="confirm-topup-btn"
              >
                {topUpMutation.isPending ? "Processing..." : `Top Up ${topUpAmount ? `J$${parseFloat(topUpAmount).toLocaleString()}` : ""}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCard && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowAddCard(false)}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg mb-4">Add Card</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Cardholder Name</Label>
                <Input
                  placeholder="Name on card"
                  className="input-glass mt-2"
                  value={cardForm.cardholder_name}
                  onChange={(e) => setCardForm({ ...cardForm, cardholder_name: e.target.value })}
                  data-testid="cardholder-name-input"
                />
              </div>
              
              <div>
                <Label>Card Number</Label>
                <Input
                  placeholder="1234 5678 9012 3456"
                  className="input-glass mt-2 font-mono"
                  value={cardForm.card_number}
                  onChange={(e) => setCardForm({ ...cardForm, card_number: e.target.value })}
                  maxLength={19}
                  data-testid="card-number-input"
                />
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Expiry</Label>
                  <Input
                    placeholder="MM/YY"
                    className="input-glass mt-2 font-mono"
                    value={cardForm.expiry}
                    onChange={(e) => setCardForm({ ...cardForm, expiry: e.target.value })}
                    maxLength={5}
                    data-testid="expiry-input"
                  />
                </div>
                <div className="flex-1">
                  <Label>CVV</Label>
                  <Input
                    type="password"
                    placeholder="•••"
                    className="input-glass mt-2 font-mono"
                    value={cardForm.cvv}
                    onChange={(e) => setCardForm({ ...cardForm, cvv: e.target.value })}
                    maxLength={4}
                    data-testid="cvv-input"
                  />
                </div>
              </div>

              <Button
                className="w-full btn-primary h-12"
                onClick={() => addMethodMutation.mutate()}
                disabled={!cardForm.card_number || !cardForm.expiry || !cardForm.cvv || addMethodMutation.isPending}
                data-testid="save-card-btn"
              >
                {addMethodMutation.isPending ? "Adding..." : "Add Card"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                A small verification charge ($0.50-$1.50) will be made to your card. You have 24 hours to confirm the exact amount.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Verify Card Modal */}
      {showVerifyCard && pendingVerification && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowVerifyCard(false);
            setPendingVerification(null);
            setVerificationAmount("");
          }}
        >
          <div 
            className="w-full max-w-sm bg-card/95 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-center mb-2">
              Verify Your Card
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Enter the exact charge amount from your card statement
            </p>

            <div className="glass rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <span className="font-mono">{pendingVerification.brand} •••• {pendingVerification.last4}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {pendingVerification.hours_remaining ? `${pendingVerification.hours_remaining} hours remaining` : "24 hours to verify"}
              </p>
            </div>

            <div className="mb-4">
              <Label>Verification Amount (USD)</Label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className="input-glass pl-8 font-mono"
                  value={verificationAmount}
                  onChange={(e) => setVerificationAmount(e.target.value)}
                  data-testid="verification-amount-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary"
                onClick={() => {
                  setShowVerifyCard(false);
                  setPendingVerification(null);
                  setVerificationAmount("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={() => verifyMethodMutation.mutate()}
                disabled={!verificationAmount || verifyMethodMutation.isPending}
                data-testid="verify-card-btn"
              >
                {verifyMethodMutation.isPending ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <NavBar active="wallet" />
    </div>
  );
};

export default VirtualCard;
